import { useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { Link, useNavigate } from 'react-router-dom';

import { Grid, Card, CardHeader, CardContent, IconButton, Button, TextField, Tooltip, CircularProgress } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { relativeTime, formatPhoneNumber, formatGenericDateTime, formatGenericDate } from '../../../utils/util';

import { BasicFlex } from '../../../utils/Common';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import NoTimeline from '../../../utils/utilities/Timeline/NoTimeline';

import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';

import Tile from '../Components/Tile';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getInventoryHistory, updateWigId } from '../apiService'

// use pageData context
import { usePageData } from '../../../contexts/PageData';

import useSnackbarStore from '../../../store/snackbar';

const StatusCard = styled(Card)`
  background-color: ${props => props.theme.colors.softBg};
  border-radius: 8px;
`;

const OrderLink = styled(Link)`
  /* text-decoration: none; */
  color: ${props => props.theme.colors.mildText};
  font-weight: 500;
  text-transform: uppercase;
`

const WigIdEditor = ({ inventoryId, wigId, onSaved }) => {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(wigId ?? '');

  const { mutate, isPending } = useMutation({
    mutationFn: (newWigId) => updateWigId(inventoryId, newWigId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      showSnackbar("Wig ID updated", "success");
      setEditing(false);
      onSaved?.();
    },
    onError: (err) => {
      showSnackbar(err?.response?.data?.message || "Failed to update Wig ID", "error");
    },
  });

  const startEdit = () => {
    setValue(wigId ?? '');
    setEditing(true);
  };

  const cancel = () => {
    setValue(wigId ?? '');
    setEditing(false);
  };

  const trimmed = value.trim();
  const canSave = !!trimmed && trimmed !== wigId && !isPending;

  const save = () => {
    if (!canSave) return;
    mutate(trimmed);
  };

  if (!editing) {
    return (
      <BasicFlex gap=".5rem" justify="flex-start">
        <span>{wigId}</span>
        <Tooltip title="Edit Wig ID">
          <IconButton size="small" color="secondary" onClick={startEdit}>
            <EditOutlined sx={{ fontSize: "1.6rem" }} />
          </IconButton>
        </Tooltip>
      </BasicFlex>
    );
  }

  return (
    <BasicFlex gap=".5rem" justify="flex-start">
      <TextField
        size="small"
        autoFocus
        value={value}
        disabled={isPending}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        sx={{ maxWidth: 220 }}
      />
      <IconButton size="small" color="success" onClick={save} disabled={!canSave}>
        {isPending ? <CircularProgress size={18} /> : <CheckRoundedIcon />}
      </IconButton>
      <IconButton size="small" onClick={cancel} disabled={isPending}>
        <CloseRoundedIcon />
      </IconButton>
    </BasicFlex>
  );
};

const DetailsTab = () => {
  const { data = {}, refetch } = usePageData();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const { data: history} = useQuery({
    queryKey: ["inventory", "history", data?.inventory_id],
    queryFn: () => getInventoryHistory(data?.inventory_id),
    enabled: !!data.inventory_id
  });

  const timeline = useMemo(() => {
    if (!history) return [];

    const hasInitialShipment = history?.[0]?.timestamp === data.date_entered

    if (!hasInitialShipment && data.date_entered) {
      const initialEvent = {
        username: data.user_created,
        timestamp: data.date_entered,
        action: 'Wig was added to inventory',
      };

      // Combine and sort by date (descending)
      return [ initialEvent, ...history]
    }

    return history;
  }, [history, data.date_entered]);

  const styleInfo = [
    {
      header: "Wig ID",
      info: (
        <WigIdEditor
          key={data.wig_id}
          inventoryId={data.inventory_id}
          wigId={data.wig_id}
          onSaved={refetch}
        />
      ),
    },
    {header: "Brand", info: <SimpleChip label={data.brand_name} uc={true} palette='secondary' />},
    { header: "Color", info: data.color },
    { header: "Length", info: data.length },
    { header: "Cap Size", info: data.cap_size },
    { header: "Type", info: data.material },
    { header: "Hairstyle", info: data.hairstyle },
    { header: "Coloring", info: data.coloring },
    { header: "SKU", info: <SimpleChip label={data.sku} uc={true} />},
  ]

  const dateInfo = [
    {header: "Date Received", info: formatGenericDateTime(data.date_entered)},
  ]

  const priceInfo = [
    {header: "Cost", info: data.cost_total, dataType: "currency"},
    {header: "Price", info: data.total_price, dataType: "currency"},
  ]

  const saleInfo = [
    {header: "Date Sold", info: formatGenericDateTime(data.date_sold)},
    {header: "Customer", info: data.customer_name},
  ]

  if (data.wig_order_num) {
    saleInfo.push({header: "Custom Order ID", info: <BasicFlex justify="flex-start" gap="1rem">
      <div>#{data.wig_order_num}</div> 
      <OrderLink to={`/wig_orders/view/${data?.wig_order_id}`}>View Custom Order</OrderLink>
      </BasicFlex>})
  }

  let location, status;

  switch (data.status) {
    case "removed":
      location = `Returned to ${data.brand_name}`;
      status = "Returned";
      break;
    case "sold":
      location = data.customer_name;
      status = <span>Sold  <span style={{ fontSize: "1.3rem", letterSpacing: "1px", fontWeight: "500"}}>({formatGenericDate(data.last_log_date, "short")})</span></span>;
      break;
    case "assigned":
      location = "Inventory";
      status = <span>Stock <span style={{ fontSize: "1.3rem", letterSpacing: "1px", fontWeight: "500"}}>(Assigned to: {data.customer_name})</span></span>;
      break;
    default:
      location = "Inventory";
      status = "In Stock";
  }

  return (
    <Grid container spacing={3}>

      <Grid size={{xs: 12}}>
        <StatusCard variant='outlined'>
          <CardContent sx={{p: 2, pb: "1.6rem !important"}}>
            <BasicFlex gap="2rem" justify="flex-start">
              <Tile Icon={LocationCityRoundedIcon} label="Status" info={status} palette='success' />
              <Tile Icon={PlaceRoundedIcon} label="Location" info={location} palette='secondary' />
            </BasicFlex>
          </CardContent>
        </StatusCard>
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Card variant='outlined'>
          <CardHeader
            title="Wig Details"
            subheader={<BasicFlex gap=".5rem" justify="flex-start"><LocalOfferRoundedIcon sx={{fontSize: "1.8rem"}} /> <span>{data.wig_id}</span></BasicFlex>}
            action={
              <BasicFlex><div>{data?.wig_order_num ? <SimpleChip label={`Custom Order`} uc={true} palette='primary' /> : null}</div> <Link to={`/inventory/edit/${data?.inventory_id}`}><IconButton color="secondary"><EditOutlined /></IconButton></Link></BasicFlex>
            }
          />
          <CardContent>
            <CardBlockContainer>
              {styleInfo.map((block) => (
                <CardBlock key={block.header} {...block } />
              ))}
              <CardSpacer />

              <CardBlockHeader>
                Shipment Info
              </CardBlockHeader>
              {dateInfo.map((block) => (
                <CardBlock key={block.header} {...block } />
              ))}
              <CardSpacer />

              <CardBlockHeader>
                Price Info
              </CardBlockHeader>
              {priceInfo.map((block) => (
                <CardBlock key={block.header} {...block } />
              ))}

              {data.status === "sold" && (
                <>
                  <CardSpacer />
                  <CardBlockHeader>
                    Sale Info
                  </CardBlockHeader>
                  {saleInfo.map((block) => (
                    <CardBlock key={block.header} {...block } />
                  ))}
                </>
              )}
            </CardBlockContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Card variant='outlined'>
          <CardHeader title={<BasicFlex gap="1rem">Wig History <HistoryRoundedIcon /></BasicFlex>} />
          <CardContent>
            <TimelineWithComments resource="inventory" resource_id={data.inventory_id} expand={true} manual={true} events={timeline} />
          </CardContent>
        </Card>
      </Grid>

    </Grid>
  );

};

export default DetailsTab;